<template>
  <v-dialog
    v-model="dialogFlag"
    max-width="650"
  >
    <v-card>
      <v-card-title>
        Edit URL
      </v-card-title>
      <v-card-text>
        <div style="display: flex; align-items: center;">
          <v-text-field
            v-model="editableUrl"
            label="Profile URL"
            :prefix="prefixUrl"
            hide-details
            required
          />
          <template v-if="url !== editableUrl">
            <v-btn
              v-if="!urlValid"
              class="ml-2"
              outlined
              :disabled="!editableUrl"
              :loading="isLoading"
              @click="checkUrl()"
            >
              Check if available
            </v-btn>
            <v-btn
              v-else
              class="ml-2"
              outlined
              color="primary"
              :loading="isLoading"
            >
              Checked
            </v-btn>
          </template>
        </div>
      </v-card-text>

      <v-card-actions
        class="justify-center pb-4"
      >
        <v-btn
          color="tertiary"
          dark
          rounded
          @click="dialogFlag = false"
        >
          Go back
        </v-btn>
        <v-btn
          color="info"
          rounded
          :disabled="!editableUrl || (url === editableUrl) || !urlValid"
          @click="toggleUseBtn"
        >
          Use this URL
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import coachApi from '@/api/coach-profile/coach';

export default {
  name: 'UrlEditor',

  props: {
    flag: {
      type: Boolean,
      default: false,
    },

    coach: {
      type: Object,
      default: null,
    },

    prefixUrl: {
      type: String,
      default: null,
    },

    url: {
      type: String,
      default: null,
    },

    originalData: {
      type: Object,
      default: () => ({
        url_index: '',
      }),
    },
  },

  data() {
    return {
      editableUrl: '',
      isLoading: false,
      urlValid: false,
    };
  },

  computed: {
    dialogFlag: {
      get() {
        if (this.flag) {
          this.getReadyToEdit();
        }
        return this.flag;
      },
      set(newValue) {
        this.$emit('update:flag', newValue);
      },
    },
  },

  watch: {
    editableUrl() {
      this.urlValid = false;
      this.setAsSlugField();
    },
  },

  methods: {
    getReadyToEdit() {
      this.editableUrl = this.url;
      this.urlValid = false;
    },

    setAsSlugField() {
      if (!this.editableUrl) return;

      const url = this.editableUrl;
      let text = url.toString().toLowerCase().trim();

      const sets = [
        { to: 'a', from: '[ÀÁÂÃÄÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶ]' },
        { to: 'c', from: '[ÇĆĈČ]' },
        { to: 'd', from: '[ÐĎĐÞ]' },
        { to: 'e', from: '[ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ]' },
        { to: 'g', from: '[ĜĞĢǴ]' },
        { to: 'h', from: '[ĤḦ]' },
        { to: 'i', from: '[ÌÍÎÏĨĪĮİỈỊ]' },
        { to: 'j', from: '[Ĵ]' },
        { to: 'ij', from: '[Ĳ]' },
        { to: 'k', from: '[Ķ]' },
        { to: 'l', from: '[ĹĻĽŁ]' },
        { to: 'm', from: '[Ḿ]' },
        { to: 'n', from: '[ÑŃŅŇ]' },
        { to: 'o', from: '[ÒÓÔÕÖØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ]' },
        { to: 'oe', from: '[Œ]' },
        { to: 'p', from: '[ṕ]' },
        { to: 'r', from: '[ŔŖŘ]' },
        { to: 's', from: '[ßŚŜŞŠ]' },
        { to: 't', from: '[ŢŤ]' },
        { to: 'u', from: '[ÙÚÛÜŨŪŬŮŰŲỤỦỨỪỬỮỰƯ]' },
        { to: 'w', from: '[ẂŴẀẄ]' },
        { to: 'x', from: '[ẍ]' },
        { to: 'y', from: '[ÝŶŸỲỴỶỸ]' },
        { to: 'z', from: '[ŹŻŽ]' },
        { to: '-', from: '[·/_,:;\']' },
        { to: '-', from: '[` ~!@#$%^&*()_=+]' },
      ];

      sets.forEach((set) => {
        text = text.replace(new RegExp(set.from, 'gi'), set.to);
      });

      this.editableUrl = text
        .replace(/\s+/g, '-') // Replace spaces with -
        .replace(/[^\w-]+/gi, '') // Remove all non-word chars
        .replace(/--+/g, '-') // Replace multiple - with single -
        .replace(/_+/g, '') // Replace all _ to -
        .replace(/__+/g, '') // Replace multiple _ with single -
        .replace(/^-+/, '') // Trim - from start of text
        .replace(/-+$/, ''); // Trim - from end of text
    },

    async checkUrl() {
      try {
        this.isLoading = true;
        const response = await coachApi.urlIndexValidate(this.editableUrl);
        this.urlValid = response.is_available;
        if (this.urlValid) {
          this.$toast.success('URL validation checked');
        } else {
          this.$toast.error('URL already exist');
        }
      } catch (err) {
        console.log(err);
        this.$toast.error(err.msg || err.message);
      } finally {
        this.isLoading = false;
      }
    },

    async toggleUseBtn() {
      try {
        if (this.urlValid) {
          if (this.originalData.url_index !== this.editableUrl) {
            await coachApi.patchProfile({ profile: { url_index: this.editableUrl } });
            this.$emit('update:originalData.url_index', this.editableUrl);
          }
          this.$toast.success('Profile URL successfully changed');
          this.$emit('update:url', this.editableUrl);
          this.dialogFlag = false;
          this.urlValid = false;
        } else {
          this.$toast.error('Check available URL first.');
        }
      } catch (err) {
        console.log(err);
        this.$toast.error(err.msg || err.message);
      }
    },
  },
};
</script>
